import React, { useState, useRef, useEffect } from "react";
import WaveSurfer from "wavesurfer.js";
import { AnimatePresence, motion } from "framer-motion";

const WaveformPlayer = ({ url, title, subtitle, descriptions, artwork }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);
  const descriptionIntervalRef = useRef(null);
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);
  const mountedRef = useRef(true);

  // Handle processing descriptions
  const processedDescriptions = Array.isArray(descriptions)
    ? descriptions
    : subtitle
    ? [subtitle]
    : [];

  // Set up auto-scrolling for descriptions
  useEffect(() => {
    // Only set up interval if there are multiple descriptions
    if (processedDescriptions.length > 1) {
      // Clear any existing interval
      if (descriptionIntervalRef.current) {
        clearInterval(descriptionIntervalRef.current);
      }

      // Set interval to cycle through descriptions every 5 seconds
      descriptionIntervalRef.current = setInterval(() => {
        setCurrentDescriptionIndex(
          (prevIndex) => (prevIndex + 1) % processedDescriptions.length
        );
      }, 5000);
    }

    // Clean up interval on unmount
    return () => {
      if (descriptionIntervalRef.current) {
        clearInterval(descriptionIntervalRef.current);
        descriptionIntervalRef.current = null;
      }
    };
  }, [processedDescriptions]);

  // Set mountedRef to false when component unmounts
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Helper to clear the container element
  const clearContainer = () => {
    if (containerRef.current) {
      // Clear any child elements that might have been created
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
    }
  };

  // Initialize wavesurfer
  useEffect(() => {
    if (!containerRef.current || !url) return;

    let shouldCancel = false;

    // Create a cleanup function
    const cleanup = () => {
      if (wavesurferRef.current) {
        try {
          // First unsubscribe from all events
          if (typeof wavesurferRef.current.unAll === "function") {
            wavesurferRef.current.unAll();
          }

          // Only try to destroy if it's a function and we're still mounted
          if (typeof wavesurferRef.current.destroy === "function") {
            wavesurferRef.current.destroy();
          }
        } catch (err) {
          // Just log the error - we're cleaning up anyway
          console.warn("Error during wavesurfer cleanup:", err);
        }

        // Clear the reference regardless of errors
        wavesurferRef.current = null;
      }

      // Always clear the container to ensure no leftover elements
      clearContainer();
    };

    // Clean up any existing instance first
    cleanup();

    // Make sure the container is empty before creating a new instance
    clearContainer();

    // Create a new instance after a small delay to allow DOM to update
    setTimeout(() => {
      // Don't proceed if we should cancel or the component is unmounting
      if (shouldCancel || !mountedRef.current || !containerRef.current) return;

      try {
        // Create WaveSurfer instance
        const wsInstance = WaveSurfer.create({
          container: containerRef.current,
          height: 32, // Reduced height for slimmer appearance
          waveColor: "#a0a0a0", // Lighter grey for the wave
          progressColor: "#6f57a5",
          cursorColor: "#e0e0e0", // Off-white cursor color
          barWidth: 2,
          barRadius: 2,
          normalize: true,
          hideScrollbar: true,
        });

        // Register event handlers
        wsInstance.on("error", (err) => {
          if (mountedRef.current) {
            console.error("WaveSurfer error:", err);
            setError(
              typeof err === "string"
                ? err
                : err?.message || "Failed to load or render audio."
            );
          }
        });

        wsInstance.on("ready", () => {
          if (mountedRef.current) {
            setError(null);
            setIsReady(true);
          }
        });

        wsInstance.on("finish", () => {
          if (mountedRef.current) {
            setIsPlaying(false);
          }
        });

        // Track play/pause state
        wsInstance.on("play", () => {
          if (mountedRef.current) {
            setIsPlaying(true);
          }
        });

        wsInstance.on("pause", () => {
          if (mountedRef.current) {
            setIsPlaying(false);
          }
        });

        // Load audio file
        wsInstance.load(url);

        // Store reference
        wavesurferRef.current = wsInstance;
      } catch (error) {
        if (mountedRef.current) {
          console.error("Error initializing WaveSurfer:", error);
          setError("Failed to initialize audio player.");
        }
      }
    }, 50);

    // Return cleanup function for useEffect
    return () => {
      shouldCancel = true;
      cleanup();
    };
  }, [url]); // Only re-run when the URL changes

  const handlePlayPause = () => {
    if (wavesurferRef.current) {
      try {
        wavesurferRef.current.playPause();
        setIsPlaying(!isPlaying);
      } catch (err) {
        console.error("Error playing/pausing:", err);
      }
    }
  };

  // Default values if props are not provided
  const displayTitle = title || "Audio Track";

  // Only render logo/artwork if provided
  const hasArtwork = !!artwork;

  return (
    <div className="audio-track-container flex flex-col rounded-lg bg-[#18181c] overflow-hidden w-full p-4">
      {/* Title and description section */}
      <div className="mb-2">
        <div className="font-semibold text-[#e0e0e0]">{displayTitle}</div>

        {processedDescriptions.length > 0 && (
          <div className="text-gray-400 mt-2 min-h-[40px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentDescriptionIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="text-sm whitespace-pre-line"
              >
                {processedDescriptions[currentDescriptionIndex]}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Playback controls and waveform */}
      <div className="flex w-full items-center">
        {/* Play button */}
        <div
          className="w-10 h-10 mr-4 flex items-center justify-center cursor-pointer relative"
          onClick={handlePlayPause}
        >
          {/* White circular border */}
          <div className="absolute inset-0 rounded-full border-2 border-white/30"></div>

          {/* Play/pause icon */}
          <div className="relative z-10">
            {isPlaying ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="7"
                  y="6"
                  width="3"
                  height="12"
                  rx="1.5"
                  fill="#6f57a5"
                />
                <rect
                  x="14"
                  y="6"
                  width="3"
                  height="12"
                  rx="1.5"
                  fill="#6f57a5"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 5v14l11-7z" fill="#6f57a5" />
              </svg>
            )}
          </div>
        </div>

        {/* Waveform container */}
        <div className="flex-grow cursor-pointer" onClick={handlePlayPause}>
          <div
            ref={containerRef}
            className="waveform-container"
            style={{ width: "100%", height: "32px" }}
          ></div>
          {error && (
            <div className="text-red-400 text-xs mt-1">
              Audio error: {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WaveformPlayer;
